import { Component, Input, OnInit, Output, ViewChild, EventEmitter } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PortalProfileModel } from 'src/portal/pages/portal-profile/portal-profile.models';
import { DialogDirective } from 'src/shared-app-portal/components/dialog/dialog.directive';
import { compare } from 'fast-json-patch';
import { PortalProfileService } from 'src/portal/pages/portal-profile/portal-profile.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
  AddressHelper,
  AddressHelperFactory
} from 'src/shared-app-portal/services/address-helper.service';
import {
  AddressService,
  AddressValidationStatus,
  IAddressValidationResult
} from 'src/shared-app-portal/services/address.service';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { AddressWarningDialogComponent } from '../address-warning.dialog/address-warning.dialog.component';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { CustomValidators } from 'src/app/shared/validators/custom.validator';

@Component({
  selector: 'update-portal-profile-dialog',
  templateUrl: './update-portal-profile.dialog.component.html',
  styleUrls: ['./update-portal-profile.dialog.component.scss'],
  standalone: false
})
export class UpdatePortalProfileDialogComponent implements OnInit {
  @Input() profile: PortalProfileModel;
  @Output() profileUpdated = new EventEmitter<PortalProfileModel>();
  protected profileForm: FormGroup<{
    firstName: FormControl<string>;
    middleName: FormControl<string>;
    lastName: FormControl<string>;
    suffix: FormControl<string>;
    email: FormControl<string>;
    phoneNumber: FormControl<string>;
    phoneNumberRaw: FormControl<string>;
    extension: FormControl<string>;
    jobTitle: FormControl<string>;
    company: FormControl<string>;
    linkedInProfile: FormControl<string>;
    streetAddress1: FormControl<string>;
    streetAddress2: FormControl<string>;
    city: FormControl<string>;
    stateId: FormControl<string>;
    postalCode: FormControl<string>;
    countryId: FormControl<string>;
    region: FormControl<string>;
    validationStatus: FormControl<AddressValidationStatus>;
    validationMessage: FormControl<string>;
    addressValidationCacheKey: FormControl<string>;
  }>;

  protected visibleField:
    | 'name'
    | 'phoneNumber'
    | 'jobTitle'
    | 'company'
    | 'address'
    | 'linkedInProfile';

  @ViewChild(DialogDirective, { static: true })
  private dialog: DialogDirective;

  @ViewChild(AddressWarningDialogComponent, { static: true })
  addressWarning: AddressWarningDialogComponent;
  private userId: string;

  addressHelper: AddressHelper;
  constructor(
    private formBuilder: FormBuilder,
    private profileService: PortalProfileService,
    private oidcSecurityService: OidcSecurityService,
    private addressHelperFactory: AddressHelperFactory,
    private addressService: AddressService,
    private appConfigService: AppConfigService
  ) {}
  ngOnInit(): void {
    this.oidcSecurityService.userData$.subscribe((data) => {
      this.userId = data?.userData?.sub || '';
    });
  }

  public editName = () => {
    this.visibleField = 'name';
    this.dialog.header = 'Name';
    this.open();
  };
  public editPhoneNumber = () => {
    this.visibleField = 'phoneNumber';
    this.dialog.header = 'Phone Number';
    this.open();
  };

  public editJobTitle = () => {
    this.visibleField = 'jobTitle';
    this.dialog.header = 'Job Title';
    this.open();
  };
  public editCompany = () => {
    this.visibleField = 'company';
    this.dialog.header = 'Company';
    this.open();
  };
  public editLinkedInProfile = () => {
    this.visibleField = 'linkedInProfile';
    this.dialog.header = 'LinkedIn Profile';
    this.open();
  };
  public editAddress = async () => {
    const validateProfileAddress =
      this.appConfigService.tenant.portalConfiguration?.validateProfileAddress ?? false;
    this.addressHelper = await this.addressHelperFactory.buildAddressHelper(
      null,
      this.userId,
      {
        Country: validateProfileAddress,
        Address1: validateProfileAddress,
        City: validateProfileAddress,
        StateOrProvince: validateProfileAddress,
        PostalCode: validateProfileAddress
      },
      {
        AddressIsShared: true
      }
    );
    const demographicInfo = this.addressHelper.buildDemographicInfo({
      countryId: this.profile?.countryId,
      stateId: this.profile?.stateId,
      isShared: false,
      postalCode: this.profile?.postalCode,
      region: this.profile?.region,
      city: this.profile?.city,
      streetAddress1: this.profile?.streetAddress1,
      streetAddress2: this.profile?.streetAddress2
    });
    this.addressHelper.updateDemographicInfo(demographicInfo);
    this.visibleField = 'address';
    this.dialog.header = 'Address';
    this.open(true);
  };

  protected save = async (): Promise<boolean> => {
    if (this.visibleField === 'address') {
      const result = await this.validateAndUpdateAddress();
      if (!result) {
        return; //keep the address modal open
      }
    }
    const profile = Object.fromEntries(
      Object.entries(this.profileForm.value).filter(
        ([key, value]) => key !== 'phoneNumberRaw' && value !== '' //consider empty strings for removal in json path
      )
    );
    const patch = compare(this.profile, profile);
    return this.profileService
      .updateProfile(patch)
      .then((updatedProfile) => {
        if (updatedProfile) {
          this.profile = updatedProfile;
          this.profileUpdated.emit(this.profile);
          this.profileService.openSnackBar('Profile updated successfully', true);
          return true;
        }
        return false;
      })
      .catch((error) => {
        console.error('Error updating profile:', error);
        return false;
      });
  };

  public useAddress = async (correctedAddress: DemographicInfo) => {
    if (correctedAddress) {
      let model = this.addressHelper.updateDemographicInfo(correctedAddress); //update the read-only address and all controls

      const validationResult = await this.addressService.validate(
        model,
        this.addressHelper.addressValidationCacheKey
      );

      model = this.addressHelper.copyFormToModel(validationResult);
      this.addressHelper.isInvalid = false; //clear Invalid status whenever address is updated
      const demographicInfo = this.addressHelper.buildDemographicInfo(model);
      this.addressHelper.updateDemographicInfo(demographicInfo);
    }
  };

  private validateAndUpdateAddress = async (): Promise<boolean> => {
    if (this.addressHelper.isInvalid) {
      this.profileService.openSnackBar('Please fill in all required address fields', false);
      return false;
    }
    if (this.appConfigService.tenant.portalConfiguration?.validateProfileAddress === true) {
      const validationResult = await this.addressService.validate(
        this.addressHelper.getModelFromForm(),
        this.addressHelper.addressValidationCacheKey
      );
      if (!validationResult) {
        return false; //probably 400/500 error on validation.
        //addressService.validate should have shown toast error
        //returning false will keep address modal open
      }

      if (validationResult.status !== 'Success') {
        const isValid = await this.openWarningModal(validationResult);
        if (isValid === false) {
          return false; //keep the address modal open
        }
      }
    }

    const updatedAddress = this.addressHelper.getModelFromForm();
    this.profileForm.patchValue({
      streetAddress1: updatedAddress.streetAddress1 != '' ? updatedAddress.streetAddress1 : null, // set to null if empty to avoid removing in in json path
      streetAddress2: updatedAddress.streetAddress2 != '' ? updatedAddress.streetAddress2 : null, // set to null if empty to avoid removing in in json path
      city: updatedAddress.city != '' ? updatedAddress.city : null, // set to null if empty to avoid removing in in json path
      stateId: updatedAddress.stateId ? updatedAddress.stateId : null, // set to null if undefined to avoid removing in in json path
      postalCode: updatedAddress.postalCode != '' ? updatedAddress.postalCode : null,
      countryId: updatedAddress.countryId ? updatedAddress.countryId : null, // set to null if undefined to avoid removing in in json path
      region: updatedAddress.region != '' ? updatedAddress.region : null, // set to null if empty to avoid removing in in json path
      addressValidationCacheKey:
        this.appConfigService.tenant.portalConfiguration?.validateProfileAddress === true
          ? this.addressHelper.addressValidationCacheKey
          : null
    });
    return true; //if address is valid, return true to close the modal
  };

  private openWarningModal = (validationResult: IAddressValidationResult): Promise<boolean> => {
    //set inputs used on the modal
    let correctedAddress: DemographicInfo = null;
    if (validationResult.correctedAddress) {
      correctedAddress = this.addressHelper
        .buildDemographicInfo(validationResult.correctedAddress)
        .updateValidationStatus('Success');
      this.addressHelper.copyFormToModel(validationResult);
    }

    //may or may not have a corrected address
    return this.addressWarning.open(
      correctedAddress,
      this.addressHelper?.address,
      validationResult.message
    );
  };

  private open = (isAddress: boolean = false) => {
    this.profileForm = this.formBuilder.group({
      firstName: [this.profile?.firstName, Validators.required],
      middleName: [this.profile?.middleName],
      lastName: [this.profile?.lastName, Validators.required],
      suffix: [this.profile?.suffix, Validators.maxLength(15)],
      email: [this.profile?.email],
      phoneNumber: [this.profile?.phoneNumber, Validators.minLength(5)],
      phoneNumberRaw: [''],
      extension: [this.profile?.extension],
      jobTitle: [this.profile?.jobTitle],
      company: [this.profile?.company],
      linkedInProfile: [this.profile?.linkedInProfile, [CustomValidators.urlValidator()]],
      streetAddress1: [this.profile?.streetAddress1],
      streetAddress2: [this.profile?.streetAddress2],
      city: [this.profile?.city],
      stateId: [this.profile?.stateId],
      postalCode: [this.profile?.postalCode],
      countryId: [this.profile?.countryId],
      region: [this.profile?.region],
      validationStatus: [this.profile?.validationStatus],
      validationMessage: [this.profile?.validationMessage],
      addressValidationCacheKey: [
        this.visibleField == 'address'
          ? this.profile?.addressValidationCacheKey || this.userId
          : null
      ]
    });
    this.dialog.width = 683;
    this.dialog.open({ form: isAddress ? this.addressHelper.addressFormGroup : this.profileForm });
  };
}
